import { useEffect, useState, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { PageHeader, StatCard, Field } from "@/components/common";
import { api, brl, errMsg, fmtDateTime, fmtQty, todayISO, ORIGIN_LABELS } from "@/lib/api";
import { toast } from "sonner";

function Filters({ f, setF, users }) {
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="card-surface grid gap-3 p-4 sm:grid-cols-3" data-testid="sold-items-filters">
      <Field label="De"><Input type="date" value={f.start} onChange={set("start")} data-testid="sold-items-start" /></Field>
      <Field label="Até"><Input type="date" value={f.end} onChange={set("end")} data-testid="sold-items-end" /></Field>
      <Field label="Usuário">
        <select value={f.user_name} onChange={set("user_name")} className="h-10 w-full rounded-md border bg-transparent px-2 text-sm" data-testid="sold-items-user">
          <option value="">Todos os usuários</option>{users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </Field>
    </div>
  );
}

function ByUser({ rows }) {
  const map = {};
  rows.forEach((r) => { const u = (map[r.user] ||= { qty: 0, total: 0 }); u.qty += r.qty; u.total += r.total; });
  const list = Object.entries(map).sort((a, b) => b[1].total - a[1].total);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="sold-items-by-user">
      {list.map(([u, v]) => <span key={u} className="rounded-full border px-3 py-1 text-xs">{u} · {fmtQty(v.qty)} itens · <b className="font-mono">{brl(v.total)}</b></span>)}
    </div>
  );
}

export default function SoldItems() {
  const [f, setF] = useState({ start: todayISO(), end: todayISO(), user_name: "" });
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    if (!f.start || !f.end) return;
    api.get("/sold-items", { params: f }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const rows = d?.rows || [];
  return (
    <>
      <PageHeader eyebrow="PDV" title="Consulta itens venda" subtitle="Itens de vendas finalizadas e quem lançou cada um. Filtre por data e usuário." />
      <Filters f={f} setF={setF} users={d?.users || []} />
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Lançamentos" value={d?.count ?? 0} testId="sold-items-count" />
        <StatCard label="Quantidade" value={fmtQty(d?.qty)} testId="sold-items-qty" />
        <StatCard label="Total" value={brl(d?.total)} tone="success" testId="sold-items-total" />
      </div>
      <ByUser rows={rows} />
      <div className="card-surface overflow-x-auto">
        <Table data-testid="sold-items-table">
          <TableHeader><TableRow><TableHead>Data / hora</TableHead><TableHead>Venda</TableHead><TableHead>Origem</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Qtd</TableHead><TableHead className="text-right">Unitário</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Usuário</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r, k) => (
              <TableRow key={k} data-testid={`sold-item-row-${k}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</TableCell>
                <TableCell className="font-mono">#{r.sale_number}</TableCell>
                <TableCell className="text-xs">{ORIGIN_LABELS[r.origin] || r.origin}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-right font-mono">{fmtQty(r.qty)}</TableCell>
                <TableCell className="text-right font-mono">{brl(r.price)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{brl(r.total)}</TableCell>
                <TableCell className="text-sm">{r.user}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">Nenhum item vendido no período.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
